import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Lock, LogOut, PackageCheck, Mail, Euro } from "lucide-react";
import { toast } from "sonner";
import { eur } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tarantina_admin_token";

const INPUT_CLS =
  "w-full rounded-lg border border-[#E2DBCB]/15 bg-[#0C0D0E] px-4 py-3 text-sm text-[#F7F5F0] placeholder:text-[#75726B] focus:outline-none focus:border-[#D4AF37]/60 transition-colors";

const STATUS_LABELS = {
  payee: ["Payée", "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10"],
  en_confection: [
    "En confection",
    "text-[#C5A059] border-[#C5A059]/40 bg-[#C5A059]/10",
  ],
  expediee: [
    "Expédiée",
    "text-emerald-300 border-emerald-300/30 bg-emerald-300/10",
  ],
  en_attente_paiement: [
    "En attente",
    "text-[#B0ABA0] border-[#E2DBCB]/20 bg-[#E2DBCB]/5",
  ],
  recue: ["Reçue", "text-[#B0ABA0] border-[#E2DBCB]/20 bg-[#E2DBCB]/5"],
};

const NEXT_STATUS = {
  payee: ["en_confection", "Passer en confection"],
  en_confection: ["expediee", "Marquer expédiée"],
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
      toast.success("Bienvenue dans votre espace");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0C0D0E] px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-[#E2DBCB]/10 bg-[#141619] p-8"
      >
        <div className="mb-8 text-center">
          <Lock size={20} className="mx-auto text-[#D4AF37]" />
          <p className="mt-4 font-serif text-2xl text-[#F7F5F0]">
            Espace <span className="italic text-[#C5A059]">Tarantina</span>
          </p>
          <p className="mt-1 text-xs font-mono uppercase tracking-[0.2em] text-[#75726B]">
            Suivi des commandes
          </p>
        </div>
        <div className="space-y-4">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={INPUT_CLS}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className={INPUT_CLS}
          />
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3 text-sm font-semibold text-[#0C0D0E] transition-colors hover:bg-[#C5A059] disabled:opacity-60"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Se connecter
          </button>
        </div>
        <a
          href="/"
          data-testid="admin-back-home"
          className="mt-6 block text-center text-xs text-[#75726B] transition-colors hover:text-[#D4AF37]"
        >
          ← Retour au site
        </a>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setData(res.data))
      .catch(() => {
        toast.error("Session expirée, reconnectez-vous");
        onLogout();
      });
  }, [token, onLogout]);

  const advanceStatus = async (orderId, status) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData((d) => ({
        ...d,
        orders: d.orders.map((o) => (o.id === orderId ? { ...o, status } : o)),
      }));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  if (!data)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0C0D0E]">
        <Loader2 size={24} className="animate-spin text-[#D4AF37]" />
      </div>
    );

  const { orders, contacts } = data;
  const paid = orders.filter((o) => o.status === "payee");
  const revenue = paid.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#0C0D0E] px-6 py-10 sm:px-12"
    >
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-serif text-2xl text-[#F7F5F0]">
              Tarantina <span className="italic text-[#C5A059]">Kimono</span>
            </p>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#75726B]">
              Suivi des commandes
            </p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="text-xs text-[#B0ABA0] transition-colors hover:text-[#D4AF37]"
            >
              Voir le site
            </a>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full border border-[#E2DBCB]/15 px-4 py-2 text-xs text-[#B0ABA0] transition-colors hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
            >
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-4">
          {[
            [<PackageCheck size={16} key="i" />, paid.length, "Commandes payées", "stat-paid"],
            [<PackageCheck size={16} key="i" />, orders.length - paid.length, "En attente", "stat-pending"],
            [<Euro size={16} key="i" />, eur(revenue), "Total encaissé", "stat-revenue"],
          ].map(([icon, value, label, tid]) => (
            <div
              key={tid}
              data-testid={tid}
              className="rounded-2xl border border-[#E2DBCB]/10 bg-[#141619] p-5"
            >
              <div className="text-[#D4AF37]">{icon}</div>
              <p className="mt-3 font-serif text-2xl text-[#F7F5F0]">{value}</p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.15em] text-[#75726B]">
                {label}
              </p>
            </div>
          ))}
        </div>

        <h2 className="mt-12 font-serif text-xl text-[#F7F5F0]">Commandes</h2>
        <div className="mt-4 space-y-3">
          {orders.length === 0 && (
            <p data-testid="orders-empty" className="text-sm text-[#75726B]">
              Aucune commande pour le moment.
            </p>
          )}
          {orders.map((o) => {
            const [label, cls] =
              STATUS_LABELS[o.status] || STATUS_LABELS.recue;
            return (
              <div
                key={o.id}
                data-testid={`order-row-${o.id}`}
                className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#E2DBCB]/10 bg-[#141619] p-5"
              >
                <div>
                  <p className="text-sm font-medium text-[#F7F5F0]">
                    {o.customer_name}
                    {o.salon ? ` — ${o.salon}` : ""}
                  </p>
                  <p className="mt-0.5 text-xs text-[#75726B]">
                    {o.email}
                    {o.phone ? ` · ${o.phone}` : ""}
                  </p>
                  <p className="mt-2 text-xs text-[#B0ABA0]">
                    {(o.items || [])
                      .map((i) => `${i.quantity}× ${i.name} (${i.size})`)
                      .join(" · ")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-lg text-[#D4AF37]">
                    {eur(o.total || 0)}
                  </p>
                  <span
                    className={`mt-1 inline-block rounded-full border px-3 py-0.5 text-[10px] font-mono uppercase tracking-widest ${cls}`}
                  >
                    {label}
                  </span>
                  {NEXT_STATUS[o.status] && (
                    <button
                      data-testid={`order-advance-${o.id}`}
                      onClick={() => advanceStatus(o.id, NEXT_STATUS[o.status][0])}
                      className="mt-2 block rounded-full border border-[#D4AF37]/40 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"
                    >
                      {NEXT_STATUS[o.status][1]}
                    </button>
                  )}
                  <p className="mt-1 text-[10px] text-[#75726B]">
                    {String(o.created_at || "").slice(0, 16).replace("T", " ")}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <h2 className="mt-12 flex items-center gap-2 font-serif text-xl text-[#F7F5F0]">
          <Mail size={16} className="text-[#D4AF37]" /> Demandes de contact
        </h2>
        <div className="mt-4 space-y-3">
          {contacts.length === 0 && (
            <p data-testid="contacts-empty" className="text-sm text-[#75726B]">
              Aucune demande pour le moment.
            </p>
          )}
          {contacts.map((c) => (
            <div
              key={c.id}
              data-testid={`contact-row-${c.id}`}
              className="rounded-xl border border-[#E2DBCB]/10 bg-[#141619] p-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-[#F7F5F0]">
                  {c.name}
                  {c.salon ? ` — ${c.salon}` : ""}
                </p>
                <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">
                  {c.request_type}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-[#75726B]">
                {c.email}
                {c.phone ? ` · ${c.phone}` : ""} ·{" "}
                {String(c.created_at || "").slice(0, 16).replace("T", " ")}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-[#B0ABA0]">
                {c.message}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(
    () => sessionStorage.getItem(TOKEN_KEY) || null
  );

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return token ? (
    <Dashboard token={token} onLogout={logout} />
  ) : (
    <Login onLogin={setToken} />
  );
}
